import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Btn } from "./UI";
import { CalendarClock, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const DigestPrefsButton = ({ clients = [] }) => {
  const [open, setOpen] = useState(false);
  const [weekday, setWeekday] = useState(0);
  const [selected, setSelected] = useState(new Set()); // empty = all clients
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    api.get("/political-os/agency/digest/prefs").then(({ data }) => {
      setWeekday(data.weekday ?? 0);
      setSelected(new Set(data.entity_ids || []));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [open, loaded]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/political-os/agency/digest/prefs", { weekday, entity_ids: [...selected] });
      toast.success(`Digest set for ${DAYS[weekday]}s — ${selected.size === 0 ? "all clients" : `${selected.size} client${selected.size === 1 ? "" : "s"}`}`);
      setOpen(false);
    } catch (e) { toast.error(errMsg(e, "Could not save digest preferences")); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Btn variant="secondary" onClick={() => setOpen(true)} data-testid="digest-prefs-btn">
        <CalendarClock className="w-4 h-4" /> Digest settings
      </Btn>
      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-app border border-white/10 rounded-lg max-w-md w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="digest-prefs-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold">Weekly digest settings</h3>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-4">Pick which day your client digest email arrives and which clients it covers.</p>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Send every</label>
            <select value={weekday} onChange={(e) => setWeekday(parseInt(e.target.value, 10))} data-testid="digest-weekday-select"
              className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none mb-4">
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Clients included</label>
              <button onClick={() => setSelected(new Set())} data-testid="digest-all-clients-btn"
                className={`text-[10px] uppercase tracking-wider ${selected.size === 0 ? "text-cyan-300" : "text-slate-500 hover:text-cyan-300"}`}>
                {selected.size === 0 ? "✓ All clients" : "Select all"}
              </button>
            </div>
            <div className="space-y-1.5 mb-5 max-h-52 overflow-y-auto pr-1">
              {clients.length === 0 ? (
                <p className="text-xs text-slate-500">No clients yet.</p>
              ) : clients.map((c) => (
                <button key={c.id} onClick={() => toggle(c.id)} data-testid={`digest-client-${c.id}`}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] transition-colors text-left">
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selected.size === 0 || selected.has(c.id) ? "bg-cyan-400 border-cyan-400" : "border-white/20"}`}>
                    {(selected.size === 0 || selected.has(c.id)) && <Check className="w-3 h-3 text-slate-900" />}
                  </span>
                  <span className="text-sm text-slate-200 truncate">{c.name}</span>
                </button>
              ))}
            </div>
            <Btn onClick={save} disabled={busy} data-testid="digest-prefs-save-btn" className="w-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save digest settings
            </Btn>
          </div>
        </div>
      )}
    </>
  );
};
